import PartForm, {
  PartFormData,
} from '@/components/Operational/Inventory/Parts/PartForm';
import { Button, Col, Form, Modal, Progress, Row, Space } from 'antd';
import { useFragaPartsContext } from './FragaPartsProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkorderItems } from '.';
import { createUploadFileObj } from '@/core/domain/archive/Archive/Archive.utils';
import { useManufacturers } from '@/core/domain/operational/Manufacturers/Manufacturers.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { FragaProduct } from '@/core/domain/operational/Fraga/Fraga.types';
import { useSubGroups } from '@/core/domain/operational/SubGroups/SubGroups.hooks';
import { useNcms } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { usePartMultipleCreate } from '@/core/domain/operational/Parts/Parts.hooks';
import { PartFlag } from '@/core/domain/operational/Parts/Parts.types';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

interface Props {
  onCancel: VoidFunction;
}

const PartsFragaCreate: React.FC<Props> = (props) => {
  const { onPrevStep, onNextStep, items, setItems } = useWorkorderItems();

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const [partsCreateValue, setPartsCreateValue] = useState<PartFormData[]>([]);

  const [form] = Form.useForm<PartFormData>();

  const { fragaParts, currentPart, setCurrentPart, setFragaParts } =
    useFragaPartsContext();

  const partMultipleCreate = usePartMultipleCreate();

  const isFirstPart = currentPart === 0;
  const hasNextPart = currentPart + 1 < fragaParts.length;
  const progressPercent = +(
    ((currentPart + 1) * 100) /
    fragaParts.length
  ).toFixed(0);

  const currentItem: FragaProduct | undefined = useMemo(
    () => fragaParts[currentPart],
    [fragaParts.length, currentPart],
  );

  const currentItemNcm = useMemo(
    () =>
      currentItem?.specifications
        ?.find((item) => item.description === 'NCM')
        ?.value.replaceAll('.', ''),
    [currentItem],
  );

  const manufacturersData = useManufacturers({
    name: currentItem?.brand.name,
  });

  const manufacturers = useMemo(
    () => flatInfiniteData(manufacturersData.data),
    [manufacturersData.data],
  );

  const subGroupsData = useSubGroups({
    name: currentItem?.productGroup?.family?.name,
  });

  const ncmData = useNcms({ code: currentItemNcm });
  const ncms = useMemo(() => flatInfiniteData(ncmData.data), [ncmData.data]);

  const partCreateValueFinded = useMemo(() => {
    return partsCreateValue.find(
      (item) => item.catalogProductId === currentItem?.productId,
    );
  }, [currentItem, partsCreateValue.length]);

  useEffect(() => {
    if (currentItem) {
      if (partCreateValueFinded) {
        form.setFieldsValue(partCreateValueFinded);
      } else {
        const image = currentItem?.images?.length
          ? createUploadFileObj(currentItem?.images[0]?.thumbnailUrl)
          : undefined;

        const comment = currentItem.specifications
          ?.map((item) => `${item.description}: ${item.value}`)
          .join(`\n`);

        form.setFieldsValue({
          manufacturingCode: currentItem.partNumber,
          catalogProductId: currentItem.productId,
          active: true,
          name: currentItem.applicationDescription,
          barCode: currentItem.specifications?.find((item) =>
            item.description.includes('(EAN)'),
          )?.value,
          imageUrl: image ? [image] : [],
          comment,
        });
      }
    }
  }, [currentItem, partCreateValueFinded, currentPart]);

  useEffect(() => {
    (async () => {
      if (
        manufacturers.length &&
        currentItem?.brand &&
        !partCreateValueFinded
      ) {
        const manufacturer = manufacturers.find(
          (item) =>
            item.name.toLocaleLowerCase() ===
            currentItem?.brand.name.toLocaleLowerCase(),
        );

        if (manufacturer) {
          form.setFieldValue('manufacturerId', manufacturer.id);
        }
      }
    })();
  }, [manufacturersData.dataUpdatedAt, partCreateValueFinded]);

  useEffect(() => {
    (async () => {
      if (
        subGroupsData.data?.data.length &&
        currentItem?.brand &&
        !partCreateValueFinded
      ) {
        const subGroup = subGroupsData.data.data.find(
          (item) =>
            item.name.toLocaleLowerCase() ===
            currentItem?.productGroup?.family.name.toLocaleLowerCase(),
        );

        if (subGroup) {
          form.setFieldValue('subGroupId', subGroup.id);
        }
      }
    })();
  }, [subGroupsData.dataUpdatedAt, partCreateValueFinded]);

  useEffect(() => {
    (async () => {
      if (ncms.length && currentItemNcm && !partCreateValueFinded) {
        const ncm = ncms.find((item) => String(item.code) === currentItemNcm);

        if (ncm) {
          form.setFieldValue('ncmId', ncm.id);
        }
      }
    })();
  }, [ncmData.dataUpdatedAt, currentItemNcm, partCreateValueFinded]);

  useEffect(() => {
    if (
      partMultipleCreate.data?.length &&
      items.length &&
      !partMultipleCreate.isLoading
    ) {
      onNextStep();
    }
  }, [items, partMultipleCreate.data, partMultipleCreate.isLoading]);

  const handleSubmit = useCallback(async () => {
    try {
      const lastPartValues = await form.validateFields();

      const hasPartValues = partsCreateValue.some(
        (item) => item.catalogProductId === lastPartValues.catalogProductId,
      );

      let partsToCreate = partsCreateValue;

      if (hasPartValues) {
        partsToCreate = partsCreateValue.map((item) => {
          if (item.catalogProductId === lastPartValues.catalogProductId) {
            return { ...item, ...lastPartValues };
          }
          return item;
        });
      } else {
        partsToCreate = [...partsCreateValue, lastPartValues];
      }

      if (partsToCreate.length) {
        const responseParts = await partMultipleCreate.mutateAsync({
          partRequestDTOList: partsToCreate.map((item) => ({
            ...item,
            imageUrl: item.imageUrl?.length
              ? item.imageUrl[0].response?.filePath || item.imageUrl[0].url
              : undefined,
            flag: PartFlag.integration,
          })),
        });

        if (responseParts?.length) {
          if (complement) {
            setItems(
              responseParts.map((item) => ({
                complementId: complement?.id,
                itemId: item.id,
                itemName: item.name,
                itemType: WorkOrderItemType.Part,
                subAreaId: item?.subGroup?.group?.subArea?.id,
                manufacturerId: item?.manufacturer?.id,
                manufacturingCode: item?.manufacturingCode,
                unitPrice: 0,
                unitCost: 0,
                quantity: 1,
                discount: 0,
                isInternal: true,
              })),
            );
            setPartsCreateValue([]);
            setFragaParts([]);
            onNextStep();
          }
        }
      }
    } catch {}
  }, [partsCreateValue]);

  const onNextPart = useCallback(async () => {
    try {
      const values = await form.validateFields();

      setPartsCreateValue((prev) => {
        const hasValues = prev.some(
          (item) => item.catalogProductId === values.catalogProductId,
        );

        if (!hasValues) {
          return [...prev, values];
        }

        return prev.map((item) => {
          if (item.catalogProductId === values.catalogProductId) {
            return { ...item, ...values };
          }
          return item;
        });
      });

      form.resetFields();

      setTimeout(() => {
        setCurrentPart((prev: number) => prev + 1);
      }, 200);
    } catch {}
  }, [partsCreateValue]);

  const onPrevPart = useCallback(
    (isDelete = false) => {
      if (isFirstPart) {
        Modal.confirm({
          title: 'Atenção',
          content: (
            <>
              <p>
                {!!isDelete &&
                  'Ao remover você voltará para a tela de seleção de peças'}
                {!isDelete &&
                  'Se você voltar para a tela de seleção de peças, todas as predefinições das peças serão perdidas.'}
              </p>
            </>
          ),
          onOk: () => onPrevStep(),
          okText: isDelete ? 'Remover' : 'Voltar',
          okButtonProps: { danger: isDelete },
        });
      } else {
        const values = form.getFieldsValue();

        setPartsCreateValue((prev) => {
          const hasValues = prev.some(
            (item) => item.catalogProductId === values.catalogProductId,
          );

          if (!hasValues) {
            return [...prev, values];
          }

          return prev.map((item) => {
            if (item.catalogProductId === values.catalogProductId) {
              return { ...item, ...values };
            }
            return item;
          });
        });

        form.resetFields();

        setCurrentPart((prev) => prev - 1);
      }
    },
    [isFirstPart, partsCreateValue],
  );

  const removePart = useCallback(() => {
    setFragaParts((prev) =>
      prev.filter(({ productId }) => productId !== currentItem.productId),
    );

    setPartsCreateValue((prev) =>
      prev.filter((item) => item.catalogProductId !== currentItem.productId),
    );

    if (fragaParts.length > 1) {
      setCurrentPart((prev) =>
        fragaParts.length > 2 ? (prev > 0 ? prev - 1 : prev + 1) : 0,
      );
    } else {
      setCurrentPart(0);
      onPrevStep();
    }
  }, [fragaParts, partsCreateValue, currentItem]);

  return (
    <div style={{ position: 'relative' }}>
      {partMultipleCreate.isLoading && (
        <ModalLoadingOverlay
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(2px)',
          }}
          messages={['Salvando peças...']}
        />
      )}

      <Progress percent={progressPercent} style={{ marginBottom: 24 }} />

      <PartForm
        key={currentPart}
        isModal
        isLoading={false}
        form={form}
        disabledFields={{
          barCode: currentItem?.specifications?.some(({ description }) =>
            description.includes('(EAN)'),
          ),
          imageUrl: !!currentItem?.images,
        }}
      />

      <Row gutter={[16, 8]} justify="space-between" style={{ marginTop: 24 }}>
        <Col>
          <span>Itens: {`${currentPart + 1}/${fragaParts.length}`}</span>
        </Col>

        <Col>
          <Space>
            <Button danger onClick={removePart}>
              Remover
            </Button>
            <Button
              onClick={() => {
                props.onCancel();
                setFragaParts([]);
                setCurrentPart(0);
              }}
            >
              Cancelar
            </Button>

            <Button onClick={() => onPrevPart()}>Voltar</Button>

            {hasNextPart && (
              <Button type="primary" onClick={onNextPart}>
                Próximo
              </Button>
            )}

            {!hasNextPart && (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={partMultipleCreate.isLoading}
              >
                Cadastrar peças
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default PartsFragaCreate;
